import { useEffect, useReducer, useRef, useState } from 'react';
import { callerIdService, IncomingCall, PendingCall } from 'services/caller-id';
import { formatPhone } from 'utils/formatPhone';

// Banner lifetime before it auto-dismisses (the call stays in the pending list).
export const BANNER_DURATION_SEC = 60;
const TOAST_DURATION_MS = 3200;

// banners are app-wide (CallerBannerHost), not part of the calendar view state
export type CallerIdView = 'fab' | 'list' | 'form';

// Unified reference to one caller (from a live banner or a pending row) used
// by the SMS/Book actions and the booking form.
export interface CallerRef {
  callerId: string;
  phone: string;
  customerId: number | null;
  customerName: string; // empty = unknown ("New customer")
  // customer was just created via the Add Customer sheet — keeps the
  // "New customer" badge on the booking page even though the caller is now known
  justCreated?: boolean;
}

export const callerRefFromIncoming = (c: IncomingCall): CallerRef => ({
  callerId: c.id,
  phone: c.phone,
  customerId: c.customer?.id ?? null,
  customerName: c.customer?.name ?? '',
});

export const callerRefFromPending = (p: PendingCall): CallerRef => ({
  callerId: p.id,
  phone: p.customerPhone,
  customerId: p.customerId,
  customerName: p.customerName,
});

interface CallerIdState {
  // source of truth for the list + badge (GET pending-caller-id)
  pending: PendingCall[];
  sheetOpen: boolean;
  activeCaller: CallerRef | null;
  toast: string | null;
}

type CallerIdAction =
  | { type: 'SET_PENDING'; pending: PendingCall[] }
  | { type: 'OPEN_SHEET' }
  | { type: 'CLOSE_SHEET' }
  | { type: 'OPEN_FORM'; caller: CallerRef }
  | { type: 'CLOSE_FORM'; toList: boolean }
  | { type: 'SHOW_TOAST'; msg: string }
  | { type: 'HIDE_TOAST' };

const initialState: CallerIdState = {
  pending: [],
  sheetOpen: false,
  activeCaller: null,
  toast: null,
};

const reducer = (state: CallerIdState, action: CallerIdAction): CallerIdState => {
  switch (action.type) {
    case 'SET_PENDING':
      return { ...state, pending: action.pending };
    case 'OPEN_SHEET':
      return { ...state, sheetOpen: true };
    case 'CLOSE_SHEET':
      return { ...state, sheetOpen: false };
    case 'OPEN_FORM':
      return { ...state, activeCaller: action.caller, sheetOpen: false };
    case 'CLOSE_FORM':
      return { ...state, activeCaller: null, sheetOpen: action.toList };
    case 'SHOW_TOAST':
      return { ...state, toast: action.msg };
    case 'HIDE_TOAST':
      return { ...state, toast: null };
    default:
      return state;
  }
};

export interface UseCallerIdResult {
  pending: PendingCall[];
  unhandledCount: number;
  view: CallerIdView;
  activeCaller: CallerRef | null;
  toast: string | null;
  showToast: (msg: string) => void;
  sendSms: (caller: CallerRef, opts?: { closeForm?: boolean; onClosed?: () => void }) => void;
  // true while an SMS request (incl. its follow-up pending refetch) is in flight
  smsLoading: boolean;
  refetchPending: () => void;
  openSheet: () => void;
  closeSheet: () => void;
  openForm: (caller: CallerRef) => void;
  closeForm: () => void;
  completeBooking: (caller: CallerRef, summary: string) => void;
}

export const useCallerId = (): UseCallerIdResult => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const mounted = useRef(true);

  const fetchSeq = useRef(0);
  const refetchPending = () => {
    const seq = ++fetchSeq.current;
    callerIdService.fetchPendingCalls()
      .then(pending => {
        // drop out-of-order responses so an older fetch can't overwrite a newer list
        if (mounted.current && seq === fetchSeq.current) dispatch({ type: 'SET_PENDING', pending });
      })
      .catch(() => {
        // keep the previous list; the next trigger/action refetches
      });
  };

  useEffect(() => {
    mounted.current = true;
    refetchPending();
    // banners are rendered app-wide by CallerBannerHost; here a ring only
    // needs to refresh the pending list (badge/sheet)
    const unsubscribeRings = callerIdService.subscribeIncomingCalls(() => refetchPending());
    // a booking completed elsewhere may have handled one of these callers
    const unsubscribeBooked = callerIdService.subscribeBookingCompleted(() => refetchPending());
    return () => {
      mounted.current = false;
      unsubscribeRings();
      unsubscribeBooked();
      clearTimeout(toastTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg: string) => {
    clearTimeout(toastTimer.current);
    dispatch({ type: 'SHOW_TOAST', msg });
    toastTimer.current = setTimeout(() => dispatch({ type: 'HIDE_TOAST' }), TOAST_DURATION_MS);
  };

  const smsInFlight = useRef<Record<string, boolean>>({});
  const [smsLoading, setSmsLoading] = useState(false);
  // opts.closeForm: SMS from the booking form behaves like Book — after the API
  // succeeds, close the form back to the calendar and show the list only when
  // pending calls remain (opts.onClosed lets the overlay reset its form state).
  const sendSms = (caller: CallerRef, opts?: { closeForm?: boolean; onClosed?: () => void }) => {
    if (smsInFlight.current[caller.callerId]) return;
    smsInFlight.current[caller.callerId] = true;
    setSmsLoading(true);
    callerIdService.sendSms(caller.callerId, caller.phone)
      .then(async () => {
        if (!mounted.current) return;
        showToast(`SMS sent to ${formatPhone(caller.phone)}`);
        if (!opts?.closeForm) return refetchPending();
        const seq = ++fetchSeq.current;
        let remaining = 0;
        try {
          const pending = await callerIdService.fetchPendingCalls();
          if (!mounted.current) return;
          if (seq === fetchSeq.current) dispatch({ type: 'SET_PENDING', pending });
          remaining = pending.length;
        } catch {
          // list unknown — fall back to the FAB state
        }
        dispatch({ type: 'CLOSE_FORM', toList: remaining > 0 });
        opts.onClosed?.();
      })
      .catch(() => {
        if (mounted.current) showToast(`Failed to send SMS to ${formatPhone(caller.phone)}`);
      })
      .finally(() => {
        delete smsInFlight.current[caller.callerId];
        if (mounted.current) setSmsLoading(false);
      });
  };

  // Book success: backend marked the caller handled via callerId → refetch,
  // close back to the calendar, and show the list only when calls remain.
  const completeBooking = (caller: CallerRef, summary: string) => {
    showToast(summary);
    const seq = ++fetchSeq.current;
    callerIdService.fetchPendingCalls()
      .then(pending => {
        if (!mounted.current) return;
        if (seq === fetchSeq.current) dispatch({ type: 'SET_PENDING', pending });
        dispatch({ type: 'CLOSE_FORM', toList: pending.length > 0 });
      })
      .catch(() => {
        if (mounted.current) dispatch({ type: 'CLOSE_FORM', toList: false });
      });
  };

  const view: CallerIdView = state.activeCaller
    ? 'form'
    : state.sheetOpen ? 'list' : 'fab';

  return {
    pending: state.pending,
    unhandledCount: state.pending.length,
    view,
    activeCaller: state.activeCaller,
    toast: state.toast,
    showToast,
    sendSms,
    smsLoading,
    refetchPending,
    openSheet: () => dispatch({ type: 'OPEN_SHEET' }),
    closeSheet: () => dispatch({ type: 'CLOSE_SHEET' }),
    openForm: (caller: CallerRef) => dispatch({ type: 'OPEN_FORM', caller }),
    closeForm: () => dispatch({ type: 'CLOSE_FORM', toList: true }),
    completeBooking,
  };
};
